import { useState, useMemo, useRef, useEffect } from 'react';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE, FONT_HEADING } from './designTokens';
import { apiClient } from '../../../api/client';

interface Zone {
  id: string;
  name: string;
  colorCode: string;
  capacity: number | null;
  sortOrder: number;
  _count: { tables: number };
}

interface TableRow {
  id: string;
  tableNumber: number;
  tableName: string;
  zoneId: string;
  zone?: { name: string; colorCode: string };
  capacity: number;
}

interface TableEditorProps {
  zones: Zone[];
  tables: TableRow[];
  onTableUpdated: (table: TableRow) => void;
  onTableCreated?: (table: TableRow) => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  filterLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  filterSelect: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.85rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    cursor: 'pointer',
    outline: 'none',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.875rem',
  },
  th: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    verticalAlign: 'middle' as const,
  },
  row: {
    transition: 'background-color 0.15s ease',
  },
  zoneCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  zoneDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2.5rem 1rem',
    gap: '0.5rem',
  },
  emptyIcon: {
    fontSize: '1.5rem',
    opacity: 0.6,
  },
  emptyText: {
    fontSize: '0.9rem',
    color: TEXT_MUTED,
    margin: 0,
  },
  tableNumber: {
    fontWeight: 600,
    color: TEXT_DARK,
    minWidth: '2rem',
  },
  editableName: {
    cursor: 'pointer',
    borderBottom: '1px dashed transparent',
    padding: '2px 4px',
    borderRadius: '3px',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
  },
  editableNameHover: {
    borderBottom: `1px dashed ${TEXT_MUTED}`,
    backgroundColor: '#f5f7f6',
  },
  editInput: {
    fontSize: '0.875rem',
    padding: '2px 6px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    width: '100%',
    maxWidth: '220px',
    color: TEXT_DARK,
    backgroundColor: BG_CARD,
  },
  zoneSelect: {
    fontSize: '0.8rem',
    padding: '0.25rem 0.5rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    cursor: 'pointer',
    outline: 'none',
    minWidth: '100px',
  },
  errorToast: {
    position: 'fixed' as const,
    bottom: '1.5rem',
    right: '1.5rem',
    backgroundColor: '#dc3545',
    color: '#fff',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 9999,
    maxWidth: '360px',
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: PRIMARY,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  addForm: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    alignItems: 'flex-end',
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  formLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  formInput: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.85rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '5px',
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    outline: 'none',
    width: '120px',
  },
  formSelect: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.85rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '5px',
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    cursor: 'pointer',
    outline: 'none',
    minWidth: '130px',
  },
  formActions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  saveButton: {
    padding: '0.45rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: PRIMARY,
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '0.45rem 0.75rem',
    fontSize: '0.82rem',
    fontWeight: 500,
    color: TEXT_MUTED,
    backgroundColor: 'transparent',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '5px',
    cursor: 'pointer',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '0.75rem',
  },
};

export function TableEditor({ zones, tables, onTableUpdated, onTableCreated }: TableEditorProps) {
  const [zoneFilter, setZoneFilter] = useState<string>('all');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [reassigningId, setReassigningId] = useState<string | null>(null);
  const [reassignError, setReassignError] = useState<string | null>(null);

  // Add Table form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState<string>('');
  const [newTableName, setNewTableName] = useState('');
  const [newZoneId, setNewZoneId] = useState('');
  const [newCapacity, setNewCapacity] = useState<string>('10');
  const [newCount, setNewCount] = useState<string>('1');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const resetAddForm = () => {
    setNewTableNumber('');
    setNewTableName('');
    setNewZoneId('');
    setNewCapacity('10');
    setNewCount('1');
    setAddError(null);
    setShowAddForm(false);
  };

  const handleAddTable = async () => {
    setAddError(null);

    const startNumber = parseInt(newTableNumber, 10);
    if (!startNumber || startNumber < 1) {
      setAddError('Starting table number is required (1+).');
      return;
    }

    const zoneId = newZoneId || (zones.length > 0 ? zones[0].id : '');
    if (!zoneId) {
      setAddError('Please select a zone.');
      return;
    }

    const capacity = parseInt(newCapacity, 10) || 10;
    const count = Math.min(Math.max(parseInt(newCount, 10) || 1, 1), 50);

    setAdding(true);
    try {
      for (let i = 0; i < count; i++) {
        const tableNumber = startNumber + i;
        const tableName = newTableName.trim()
          ? (count > 1 ? `${newTableName.trim()} ${tableNumber}` : newTableName.trim())
          : `Table ${tableNumber}`;
        const created = await apiClient<TableRow>('/table-management/tables', {
          method: 'POST',
          body: { tableNumber, tableName, zoneId, capacity },
        });
        onTableCreated?.(created);
      }
      resetAddForm();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to create table';
      setAddError(message);
    } finally {
      setAdding(false);
    }
  };

  const handleZoneReassign = async (table: TableRow, newZoneId: string) => {
    if (newZoneId === table.zoneId) return;

    const previousZoneId = table.zoneId;
    const previousZone = table.zone;

    // Find the new zone details for optimistic update
    const newZone = zones.find((z) => z.id === newZoneId);

    // Optimistic UI update
    onTableUpdated({
      ...table,
      zoneId: newZoneId,
      zone: newZone ? { name: newZone.name, colorCode: newZone.colorCode } : table.zone,
    });

    setReassigningId(table.id);
    setReassignError(null);

    try {
      await apiClient('/table-management/tables/' + table.id + '/reassign-zone', {
        method: 'POST',
        body: { newZoneId },
      });
      // Success — optimistic update already applied
    } catch (err: unknown) {
      // Revert to previous zone on failure
      onTableUpdated({
        ...table,
        zoneId: previousZoneId,
        zone: previousZone,
      });
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to reassign zone';
      setReassignError(message);
      setTimeout(() => setReassignError(null), 4000);
    } finally {
      setReassigningId(null);
    }
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const startEdit = (table: TableRow) => {
    setEditingId(table.id);
    setEditValue(table.tableName);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingId) return;

    const table = tables.find((t) => t.id === editingId);
    if (!table) {
      cancelEdit();
      return;
    }

    const trimmedValue = editValue.trim();

    // If unchanged, just cancel
    if (trimmedValue === table.tableName) {
      cancelEdit();
      return;
    }

    try {
      const updatedTable = await apiClient<TableRow>('/table-management/tables/' + editingId, {
        method: 'PATCH',
        body: { tableName: trimmedValue },
      });
      onTableUpdated(updatedTable);
      setEditingId(null);
      setEditValue('');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to rename table';
      alert(message);
      // Revert to previous value
      cancelEdit();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  // Sort tables by tableNumber ascending and apply zone filter
  const filteredTables = useMemo(() => {
    const sorted = [...tables].sort((a, b) => a.tableNumber - b.tableNumber);
    if (zoneFilter === 'all') return sorted;
    return sorted.filter((t) => t.zoneId === zoneFilter);
  }, [tables, zoneFilter]);

  return (
    <div style={styles.container}>
      {/* Add Table button and form */}
      <div style={styles.topBar}>
        {!showAddForm && (
          <button
            style={styles.addButton}
            onClick={() => {
              setShowAddForm(true);
              if (zones.length > 0 && !newZoneId) setNewZoneId(zones[0].id);
            }}
            aria-label="Add a new table"
          >
            + Add Table
          </button>
        )}
      </div>

      {showAddForm && (
        <div style={styles.addForm}>
          <div style={styles.formField}>
            <label style={styles.formLabel} htmlFor="new-table-number">Starting #</label>
            <input
              id="new-table-number"
              type="number"
              min={1}
              max={200}
              value={newTableNumber}
              onChange={(e) => setNewTableNumber(e.target.value)}
              style={{ ...styles.formInput, width: '80px' }}
              placeholder="1"
              required
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.formLabel} htmlFor="new-table-name">Name (optional)</label>
            <input
              id="new-table-name"
              type="text"
              maxLength={100}
              value={newTableName}
              onChange={(e) => setNewTableName(e.target.value)}
              style={styles.formInput}
              placeholder="e.g. VIP-1"
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.formLabel} htmlFor="new-table-zone">Zone</label>
            <select
              id="new-table-zone"
              value={newZoneId}
              onChange={(e) => setNewZoneId(e.target.value)}
              style={styles.formSelect}
            >
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>{zone.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formField}>
            <label style={styles.formLabel} htmlFor="new-table-capacity">Seats</label>
            <input
              id="new-table-capacity"
              type="number"
              min={1}
              max={20}
              value={newCapacity}
              onChange={(e) => setNewCapacity(e.target.value)}
              style={{ ...styles.formInput, width: '70px' }}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.formLabel} htmlFor="new-table-count">Count</label>
            <input
              id="new-table-count"
              type="number"
              min={1}
              max={50}
              value={newCount}
              onChange={(e) => setNewCount(e.target.value)}
              style={{ ...styles.formInput, width: '70px' }}
              title="Number of tables to create (sequential from starting #)"
            />
          </div>
          <div style={styles.formActions}>
            <button
              style={{ ...styles.saveButton, opacity: adding ? 0.6 : 1 }}
              onClick={handleAddTable}
              disabled={adding}
            >
              {adding ? 'Saving…' : 'Save'}
            </button>
            <button style={styles.cancelButton} onClick={resetAddForm} disabled={adding}>
              Cancel
            </button>
          </div>
          {addError && (
            <div style={{ width: '100%', color: '#c62828', fontSize: '0.8rem', marginTop: '0.25rem' }} role="alert">
              {addError}
            </div>
          )}
        </div>
      )}

      {/* Zone filter dropdown */}
      <div style={styles.filterRow}>
        <label htmlFor="zone-filter" style={styles.filterLabel}>
          Filter by Zone:
        </label>
        <select
          id="zone-filter"
          style={styles.filterSelect}
          value={zoneFilter}
          onChange={(e) => setZoneFilter(e.target.value)}
        >
          <option value="all">All Zones</option>
          {zones.map((zone) => (
            <option key={zone.id} value={zone.id}>
              {zone.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table list or empty state */}
      {filteredTables.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>🪑</span>
          <p style={styles.emptyText}>
            {tables.length === 0
              ? 'No tables have been created yet.'
              : 'No tables match the selected zone filter.'}
          </p>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Table Name</th>
              <th style={styles.th}>Zone</th>
              <th style={styles.th}>Capacity</th>
            </tr>
          </thead>
          <tbody>
            {filteredTables.map((table) => (
              <tr
                key={table.id}
                style={{
                  ...styles.row,
                  backgroundColor: hoveredRow === table.id ? '#f5f7f6' : 'transparent',
                }}
                onMouseEnter={() => setHoveredRow(table.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td style={{ ...styles.td, ...styles.tableNumber }}>{table.tableNumber}</td>
                <td style={styles.td}>
                  {editingId === table.id ? (
                    <input
                      ref={inputRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onBlur={saveEdit}
                      maxLength={100}
                      style={styles.editInput}
                      aria-label={`Edit name for table ${table.tableNumber}`}
                    />
                  ) : (
                    <span
                      style={{
                        ...styles.editableName,
                        ...(hoveredName === table.id ? styles.editableNameHover : {}),
                      }}
                      onClick={() => startEdit(table)}
                      onMouseEnter={() => setHoveredName(table.id)}
                      onMouseLeave={() => setHoveredName(null)}
                      title="Click to edit"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          startEdit(table);
                        }
                      }}
                    >
                      {table.tableName}
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  <div style={styles.zoneCell}>
                    <span
                      style={{
                        ...styles.zoneDot,
                        backgroundColor: table.zone?.colorCode || '#999',
                      }}
                    />
                    <select
                      style={{
                        ...styles.zoneSelect,
                        opacity: reassigningId === table.id ? 0.6 : 1,
                      }}
                      value={table.zoneId}
                      onChange={(e) => handleZoneReassign(table, e.target.value)}
                      disabled={reassigningId === table.id}
                      aria-label={`Zone for table ${table.tableNumber}`}
                    >
                      {zones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
                <td style={styles.td}>{table.capacity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Zone reassignment error toast */}
      {reassignError && (
        <div style={styles.errorToast} role="alert">
          {reassignError}
        </div>
      )}
    </div>
  );
}
